import { Promotion } from "./types"
import { DealTerms, format_money, format_pct, format_units, line_totals, participation_share, trade_spend, unit_discount } from "./deal_math"
import { promotion_type_layout } from "./promotion_type_layout"

export { participation_share, format_pct } from "./deal_math"

/**
 * Plan-time assumptions a promotion carries before any actuals exist for the retailer.
 *
 * - `expected_lift_pct`: extra volume expected over baseline, in percent (20 = +20%).
 * - `participation_pct`: share of units the deal is actually paid on, in percent. Blank means every
 *   store runs the deal (100%). Chains whose stores opt in individually (Hy-Vee) run well below that,
 *   so a $1.00/unit rate must not be planned against every unit sold in the window.
 *
 * The per-unit cost of the deal comes from `deal_math::unit_discount`, so a %, a Multi-Buy or a
 * BOGO estimates once the promotion has a regular price. Money math is done in cents.
 */
export type PlanningAssumptions = DealTerms & Pick<Promotion, "planned_volume_units" | "participation_pct" | "expected_lift_pct">

/**
 * PURE: what to tell the rep about the Expected trade estimate's rate, or null when there is
 * nothing to say (Bakhrom: "Discount % is ignored when Discount $ is also filled … It should …
 * say which one wins").
 */
export function expected_trade_note(inputs: PlanningAssumptions): string | null {
  const layout = promotion_type_layout(inputs.promotion_type)
  const has_price = Boolean(inputs.regular_price)
  if (layout.deal_fields === "discount") {
    const has_amount = Boolean(inputs.discount_amount)
    const has_pct = Boolean(inputs.discount_percentage)
    if (has_amount && has_pct) {
      return has_price
        ? "The estimate uses the $ per unit; the % is saved for reference."
        : "The estimate uses the $ per unit. The % is saved for reference — enter a regular price to check the two agree."
    }
    if (has_pct && !has_amount && !has_price) {
      return "A discount % alone can't be estimated until there is a regular price to take it off. Enter the regular price, or the $ per unit."
    }
    return null
  }
  if (layout.deal_fields === "multibuy" && inputs.multibuy_quantity && inputs.multibuy_price && !has_price) {
    return "Enter the regular price and the discount per unit is worked out from the deal price."
  }
  if (layout.deal_fields === "bogo" && inputs.bogo_buy_quantity && inputs.bogo_get_quantity && !has_price && !inputs.bogo_free_unit_cost) {
    return "Enter the regular price (or the cost of a free unit) and the deal's cost per unit sold is worked out."
  }
  return null
}

/**
 * Expected trade spend in dollars: per-unit cost of the deal × planned units × participation share.
 * Null when the deal has no per-unit cost yet or there is no planned unit volume to multiply.
 */
export function expected_trade_spend(inputs: PlanningAssumptions): number | null {
  const discount = unit_discount(inputs)
  const units = inputs.planned_volume_units
  if (!discount || !units || !Number.isFinite(units) || discount.amount <= 0) return null
  return trade_spend(discount.amount, units, inputs.participation_pct)
}

/**
 * PURE: the promotion's expected trade — the per-product rows' spend when they carry one, else the
 * header's rate × volume. What the form's estimate box and the summary panel both show.
 */
export function promotion_estimate(promotion: Pick<Promotion, "lines"> & PlanningAssumptions): number | null {
  const lines = promotion.lines ?? []
  if (lines.length > 0) {
    const totals = line_totals(lines, promotion)
    if (totals.spend > 0) return totals.spend
  }
  return expected_trade_spend(promotion)
}

/** "$1.00 × 60,000 units × 35% of stores" — the working shown next to the estimate. */
export function format_trade_spend_formula(inputs: PlanningAssumptions): string {
  const discount = unit_discount(inputs)
  const rate = discount ? (discount.working ? `${format_money(discount.amount)} (${discount.working})` : format_money(discount.amount)) : "$0.00"
  const parts = [rate, format_units(inputs.planned_volume_units ?? 0)]
  if (inputs.participation_pct != null && Number.isFinite(inputs.participation_pct)) {
    parts.push(`${format_pct(inputs.participation_pct)} of stores`)
  }
  return parts.join(" × ")
}

/** Validation message for the participation field, or null when acceptable (blank is fine). */
export function participation_validation_error(participation_pct: number | undefined): string | null {
  if (participation_pct == null) return null
  if (!Number.isFinite(participation_pct) || participation_pct <= 0 || participation_pct > 100) {
    return "Store participation must be between 0 and 100 percent (leave blank when every store runs the deal)"
  }
  return null
}

/** Validation message for the expected lift field, or null when acceptable (blank is fine). */
export function expected_lift_validation_error(expected_lift_pct: number | undefined): string | null {
  if (expected_lift_pct == null) return null
  if (!Number.isFinite(expected_lift_pct) || expected_lift_pct < 0) {
    return "Expected lift must be zero or a positive percentage"
  }
  return null
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe("participation_share", () => {
    it("treats blank as every store and clamps to 0–100", () => {
      expect(participation_share(undefined)).toBe(1)
      expect(participation_share(35)).toBeCloseTo(0.35)
      expect(participation_share(150)).toBe(1)
      expect(participation_share(-5)).toBe(0)
    })
  })

  describe("expected_trade_spend", () => {
    it("multiplies rate × units × participation in cents", () => {
      // Adam's Hy-Vee case: $1.00/unit, 60,000 units, ~35% of stores opt in.
      expect(expected_trade_spend({ promotion_type: "TPR", discount_amount: 1, planned_volume_units: 60_000, participation_pct: 35 })).toBe(21_000)
      // Blank participation means every store.
      expect(expected_trade_spend({ promotion_type: "TPR", discount_amount: 1, planned_volume_units: 60_000 })).toBe(60_000)
      // No float drift on awkward rates.
      expect(expected_trade_spend({ promotion_type: "TPR", discount_amount: 0.1, planned_volume_units: 3, participation_pct: 100 })).toBe(0.3)
    })

    it("estimates a %, a Multi-Buy and a BOGO once there is a regular price", () => {
      expect(expected_trade_spend({ promotion_type: "TPR", discount_percentage: 15, planned_volume_units: 10_000 })).toBeNull()
      expect(expected_trade_spend({ promotion_type: "TPR", discount_percentage: 15, regular_price: 3.99, planned_volume_units: 10_000 })).toBe(6_000)
      expect(
        expected_trade_spend({ promotion_type: "Multi-Buy", multibuy_quantity: 2, multibuy_price: 6, regular_price: 3.99, planned_volume_units: 1_000 }),
      ).toBe(990)
      expect(expected_trade_spend({ promotion_type: "BOGO", bogo_buy_quantity: 1, bogo_get_quantity: 1, regular_price: 3.99, planned_volume_units: 1_000 })).toBe(
        2_000,
      )
    })

    it("is null without a dollar rate or a unit volume", () => {
      expect(expected_trade_spend({ promotion_type: "TPR", planned_volume_units: 100 })).toBeNull()
      expect(expected_trade_spend({ promotion_type: "TPR", discount_amount: 1 })).toBeNull()
      expect(expected_trade_spend({ promotion_type: "TPR", discount_amount: 0, planned_volume_units: 100 })).toBeNull()
    })
  })

  describe("promotion_estimate", () => {
    it("prefers the per-product rows' spend, else the header's rate × volume", () => {
      const header = { promotion_type: "TPR", discount_amount: 1, planned_volume_units: 1_000 }
      expect(promotion_estimate(header)).toBe(1_000)
      expect(promotion_estimate({ ...header, lines: [{ discount_amount: 2, planned_volume_units: 100 }] })).toBe(200)
      // Rows with no spend yet fall back to the header.
      expect(promotion_estimate({ ...header, lines: [{ product_name: "x" }] })).toBe(1_000)
    })
  })

  describe("format_trade_spend_formula", () => {
    it("shows the working, with participation only when set", () => {
      expect(format_trade_spend_formula({ promotion_type: "TPR", discount_amount: 1, planned_volume_units: 60_000, participation_pct: 35 })).toBe(
        "$1.00 × 60,000 units × 35% of stores",
      )
      expect(format_trade_spend_formula({ promotion_type: "TPR", discount_amount: 0.5, planned_volume_units: 1_200 })).toBe("$0.50 × 1,200 units")
      expect(format_trade_spend_formula({ promotion_type: "TPR", discount_percentage: 15, regular_price: 3.99, planned_volume_units: 1_000 })).toBe(
        "$0.60 (15% of $3.99) × 1,000 units",
      )
    })
  })

  describe("expected_trade_note", () => {
    it("says the $ wins when both are filled", () => {
      expect(expected_trade_note({ promotion_type: "TPR", discount_amount: 1.5, discount_percentage: 15, planned_volume_units: 10_000 })).toMatch(/uses the \$ per unit/)
    })
    it("asks for a regular price when a % alone cannot be estimated, and goes quiet once there is one", () => {
      expect(expected_trade_note({ promotion_type: "TPR", discount_percentage: 15, planned_volume_units: 10_000 })).toMatch(/regular price/)
      expect(expected_trade_note({ promotion_type: "TPR", discount_percentage: 15, regular_price: 3.99 })).toBeNull()
    })
    it("asks a Multi-Buy and a BOGO for the regular price", () => {
      expect(expected_trade_note({ promotion_type: "Multi-Buy", multibuy_quantity: 2, multibuy_price: 6 })).toMatch(/regular price/)
      expect(expected_trade_note({ promotion_type: "BOGO", bogo_buy_quantity: 1, bogo_get_quantity: 1 })).toMatch(/regular price/)
      expect(expected_trade_note({ promotion_type: "BOGO", bogo_buy_quantity: 1, bogo_get_quantity: 1, bogo_free_unit_cost: 2 })).toBeNull()
    })
    it("says nothing when only one rate is in play", () => {
      expect(expected_trade_note({ promotion_type: "TPR", discount_amount: 1.5, planned_volume_units: 10_000 })).toBeNull()
    })
  })

  describe("validation", () => {
    it("accepts blank, rejects out-of-range participation", () => {
      expect(participation_validation_error(undefined)).toBeNull()
      expect(participation_validation_error(35)).toBeNull()
      expect(participation_validation_error(0)).not.toBeNull()
      expect(participation_validation_error(101)).not.toBeNull()
    })
    it("accepts blank or non-negative lift", () => {
      expect(expected_lift_validation_error(undefined)).toBeNull()
      expect(expected_lift_validation_error(0)).toBeNull()
      expect(expected_lift_validation_error(20)).toBeNull()
      expect(expected_lift_validation_error(-5)).not.toBeNull()
    })
  })
}
